import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { useHaptics, type HapticStyle } from "@/hooks/useHaptics";
import { DEFAULT_BUTTON_HAPTIC } from "@/lib/haptics";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 dark-premium:bg-gradient-primary dark-premium:hover:shadow-glow-primary dark-premium:transition-all dark-premium:duration-300",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-11 px-4 py-2", // iOS 44pt mínimo
        sm: "h-10 rounded-md px-3",
        lg: "h-12 rounded-md px-8",
        icon: "h-11 w-11", // 44pt touch target
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  haptic?: HapticStyle | boolean;
  disableHaptic?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, haptic, disableHaptic, onClick, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    const haptics = useHaptics();

    const handleClick = React.useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
      // Don't trigger haptic if disabled or explicitly disabled
      if (disabled || disableHaptic) {
        onClick?.(e);
        return;
      }

      // Determine haptic style
      let hapticStyle: HapticStyle = DEFAULT_BUTTON_HAPTIC;
      
      if (haptic === false) {
        // Explicitly disabled
        onClick?.(e);
        return;
      } else if (typeof haptic === 'string') {
        hapticStyle = haptic;
      } else if (haptic === true) {
        hapticStyle = DEFAULT_BUTTON_HAPTIC;
      }

      // Trigger haptic feedback
      haptics.trigger(hapticStyle);

      // Call original onClick
      onClick?.(e);
    }, [disabled, disableHaptic, haptic, haptics, onClick]);

    return (
      <Comp 
        className={cn(buttonVariants({ variant, size, className }))} 
        ref={ref} 
        onClick={handleClick}
        disabled={disabled}
        {...props} 
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
